<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.dataAccessRestrictions.dataAccessRestrictions') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add', {})"
            :tooltip="$i18n.t('admin.dataAccessRestrictions.addDataAccessRestriction')"
          />
        </v-col>
        <v-spacer />
        <data-input v-model="searchValue" v-bind="searchInput" @input="search" />
      </v-row>
      <v-row no-gutters class="pt-7" align="center">
        <v-col cols="12" style="border: 1px solid rgba(0, 0, 0, 0.12);">
          <v-data-table
            :items="searchedDataAccessRestrictionsMapped"
            :headers="headers"
            :footer-props="{
              itemsPerPageOptions: [20, 50, 100],
            }"
            :items-per-page="20"
            sort-by="id"
            :sort-desc="true"
          >
            <template #[`item.actions`]="{ item }">
              <dots-menu :items="dotsItems" v-on="getDataTreeDotsListeners(item)" />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      ref="main-input-dialog"
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template v-for="(input, idx) of mainInputDialogInputs" #[`${input.name}.input`]="data">
        <v-col cols="6" :key="idx">
          <data-input v-bind="data" v-model="currentActionData[input.name]">
            <template v-if="input.name === 'value'" #selection="{ item, index }">
              <v-chip v-if="index < 3">
                <span class="d-inline-block text-truncate">{{ item.text || item }}</span>
              </v-chip>
              <span v-if="index === 3" class="text-caption grey--text">
                {{ `(+${$i18n.tc('admin.other', currentActionData[input.name].length - 3)})` }}
              </span>
            </template>
          </data-input>
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { debounce, get as lodashGet } from 'lodash';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'DataAccessRestrictionsSettingsList',
  components: {
    DataInput,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  computed: {
    deleteUserSufix() {
      return `(${this.$store.get('admin/modulesMapping@users.deleted_suffix')})`;
    },
    usersGroups: get('users/groups'),
    usersGroupsArray() {
      return this.$_objectToArray(this.usersGroups).map(group => {
        return { text: group.name, value: group.id };
      });
    },
    dataSources: get('layers/metadata'),
    dataSourcesNamesArray() {
      return this.$_objectToArray(this.dataSources || [])
        .filter(dataSource => dataSource.scope === 'core')
        .map(dataSource => {
          return { value: dataSource.name, text: dataSource.verbose_name };
        });
    },
    relationValuesMapping: get('attributesSchema/relationValuesMapping'),

    dataAccessRestrictions: get('dataAccessRestrictions/dataAccessRestrictions'),
    dataAccessRestrictionsArray() {
      return this.$_objectToArray(this.dataAccessRestrictions);
    },
    dataAccessRestrictionsMapped() {
      return this.dataAccessRestrictionsArray.map(aR => {
        const { datasource, filter_expression, group_id, id } = aR;
        const filter = filter_expression?.['$IN'] || {};
        const filterDataSourceAttribute = Object.keys(filter)?.[0] || '';
        const attribute = filterDataSourceAttribute.split('.')?.[1] || '';
        const value = Object.values(filter)?.[0] || [];
        let mappedValue = value;

        const attributeData = (this.dataSources[datasource]?.attributes_schema.attributes || []).find(
          att => att.name === attribute
        );
        if (attributeData?.relation) {
          let uniques = this.relationValuesMapping[attributeData.relation.data_source]?.[
            attributeData.relation.attribute
          ]?.[attributeData.relation.representation];
          if (attributeData.relation.data_source === 'users_users')
            uniques = uniques.filter(item => !item.text.includes(this.deleteUserSufix));
          mappedValue = value.map(v => uniques.find(u => u.value === v)?.text || v);
        }

        return {
          id,
          group_id,
          datasource,
          attribute,
          value,
          verboses: {
            group_id: this.usersGroups[group_id]?.name || '',
            datasource: this.dataSources[datasource]?.verbose_name || '',
            attribute:
              this.dataSources[datasource]?.attributes_schema.attributes.find(a => a.name === attribute)
                ?.verbose_name || '',
            value: mappedValue.join(', '),
          },
        };
      });
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },

    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
        },
      };
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(
                `admin.dataAccessRestrictions.actions.${this.currentAction}DataAccessRestrictionTitle`
              ),
              body: this.$i18n.t(
                `admin.dataAccessRestrictions.actions.confirmDataAccessRestriction${this.capitalizeCurrentAction}`
              ),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(
                `admin.dataAccessRestrictions.actions.${this.currentAction}DataAccessRestrictionTitle`
              ),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'group_id',
          translationPath: 'admin.dataAccessRestrictions.attributes.group',
          subtitle: 'admin.dataAccessRestrictions.attributes.groupSubtitle',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.usersGroupsArray,
        },
        {
          name: 'datasource',
          translationPath: 'admin.dataAccessRestrictions.attributes.datasource',
          subtitle: 'admin.dataAccessRestrictions.attributes.datasourceSubtitle',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesNamesArray,
        },
        {
          name: 'attribute',
          translationPath: 'admin.dataAccessRestrictions.attributes.attribute',
          subtitle: 'admin.dataAccessRestrictions.attributes.attributeSubtitle',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes,
          disabled: !this.currentDataSourceAttributes || !this.currentActionData?.['datasource'],
        },
        {
          name: 'value',
          translationPath: 'admin.dataAccessRestrictions.attributes.value',
          subtitle: 'admin.dataAccessRestrictions.attributes.valueSubtitle',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          multiple: true,
          items: this.currentAttributeValues,
          disabled: !this.currentAttributeValues || !this.currentActionData?.['attribute'],
          loading: this.currentAttributeValuesLoading,
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs.map(input => {
          if (input.name === 'group_id' || input.name === 'datasource') {
            return { ...input, disabled: true };
          }
          return input;
        }),
      };
      return mainInputDialogInputsDict[this.currentAction];
    },

    headers() {
      return [
        {
          value: 'id',
          text: 'ID',
          sortable: true,
          align: 'center',
        },
        {
          value: 'verboses.group_id',
          text: this.$i18n.t('admin.dataAccessRestrictions.attributes.group'),
          align: 'center',
          searchable: true,
        },
        {
          value: 'verboses.datasource',
          text: this.$i18n.t('admin.dataAccessRestrictions.attributes.datasource'),
          align: 'center',
          searchable: true,
        },
        {
          value: 'verboses.attribute',
          text: this.$i18n.t('admin.dataAccessRestrictions.attributes.attribute'),
          align: 'center',
          searchable: true,
        },
        {
          value: 'verboses.value',
          text: this.$i18n.t('admin.dataAccessRestrictions.attributes.value'),
          align: 'center',
          searchable: true,
        },
        { text: '', value: 'actions', sortable: false },
      ];
    },
    searchableHeadersValues() {
      return this.headers.filter(header => header.searchable).map(header => header.value);
    },
    searchInput() {
      return {
        dataType: { name: 'text' },
        placeholder: this.$i18n.t('default.find'),
        appendIcon: 'mdi-magnify',
        hideDetails: true,
      };
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,

    currentDataSource: '',
    currentDataSourceAttributes: undefined,
    currentAttributeValues: undefined,
    currentAttributeValuesLoading: false,

    dotsItems: [{ name: 'edit' }, { name: 'delete' }],

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,

    searchValue: '',
    searchedDataAccessRestrictionsMapped: [],
  }),
  methods: {
    ...call('dataAccessRestrictions/*DataAccessRestriction'),
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),

    getDataTreeDotsListeners(item) {
      return this.dotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = JSON.parse(JSON.stringify(value || {}));
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction(value);
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}DataAccessRestriction`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteDataAccessRestriction() {
      const { datasource, group_id } = this.currentActionData;
      await this.deleteDataAccessRestriction({ datasource, group_id });
      this.search();
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddDataAccessRestriction() {
      const { datasource, attribute, value, group_id } = this.currentActionData;
      const payload = {
        datasource,
        filter_expression: {
          $IN: {
            [this.dataSources[datasource]?.attributes_use_datasource_qualified_names
              ? attribute
              : `${datasource}.${attribute}`]: value,
          },
        },
        group_id,
      };
      await this.addDataAccessRestriction(payload);
      this.search();
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditDataAccessRestriction() {
      const { datasource, attribute, value, group_id } = this.currentActionData;
      const payload = {
        filter_expression: {
          $IN: {
            [this.dataSources[datasource]?.attributes_use_datasource_qualified_names
              ? attribute
              : `${datasource}.${attribute}`]: value,
          },
        },
      };
      await this.editDataAccessRestriction({
        group_id,
        datasource,
        payload,
      });
      this.search();
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },

    setCurrentDataSourceAttributes(value, clearDeep = false) {
      if (!value) {
        this.currentDataSourceAttributes = [];
        return;
      }
      if (clearDeep) {
        delete this.currentActionData['attribute'];
        delete this.currentActionData['value'];
        this.$refs['main-input-dialog']?.$refs['form']?.resetValidation();
      }
      this.currentDataSourceAttributes = (this.dataSources[value]?.attributes_schema.attributes || [])
        .filter(attribute => attribute.data_type?.name !== 'geometric')
        .map(attribute => {
          return {
            value: attribute.name,
            text: attribute.verbose_name,
          };
        });
    },
    async setCurrentAttributeValues(value, clearDeep = false) {
      if (!value) {
        this.currentAttributeValues = [];
        return;
      }
      if (clearDeep) {
        delete this.currentActionData['value'];
        this.$refs['main-input-dialog']?.$refs['form']?.resetValidation();
      }
      this.currentAttributeValues = await this.getValuesOfAttribute(this.currentActionData.datasource, value);
    },
    async getValuesOfAttribute(dataSourceName, attributeName) {
      const attribute = (this.dataSources[dataSourceName]?.attributes_schema.attributes || []).find(
        attribute => attribute.name === attributeName
      );
      let uniques = [];
      try {
        this.currentAttributeValuesLoading = true;
        if (attribute.dict_id || (attribute.dict_id === 0 && attribute.allowed_values)) {
          uniques = attribute.allowed_values;
        } else if (attribute.relation) {
          uniques = this.relationValuesMapping[attribute.relation.data_source]?.[attribute.relation.attribute]?.[
            attribute.relation.representation
          ];
          if (attribute.relation.data_source === 'users_users')
            uniques = uniques.filter(item => !item.text.includes(this.deleteUserSufix));
        } else {
          const r = await this.getDataSourceAttributeUniqueValues({ dataSourceName, attributeName });
          uniques = Object.keys(r.values);
        }
      } finally {
        this.currentAttributeValuesLoading = false;
      }
      return uniques;
    },
    search(value = this.searchValue) {
      if (!value) this.searchedDataAccessRestrictionsMapped = [...this.dataAccessRestrictionsMapped];
      else
        this.searchedDataAccessRestrictionsMapped = [...this.dataAccessRestrictionsMapped].filter(dict => {
          return this.searchableHeadersValues.some(header => {
            return lodashGet(dict, header)
              ?.toLocaleLowerCase()
              .includes(value?.toLocaleLowerCase());
          });
        });
    },
  },
  watch: {
    'currentActionData.datasource': {
      handler(nV, oV) {
        this.setCurrentDataSourceAttributes(nV, oV ? true : false);
      },
    },
    'currentActionData.attribute': {
      handler(nV, oV) {
        if (!this.isMainInputDialogVisible) return;
        this.setCurrentAttributeValues(nV, oV ? true : false);
      },
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentDataSourceAttributes = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
    this.search();
    this.search = debounce(this.search, 500);
  },
};
</script>
